import { useEffect, useRef } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';
import { v4 as uuidv4 } from 'uuid';

import useMapContext from 'components/Map/hooks/useMapContext';
import useMapEvents from 'components/Map/hooks/useMapEvents';

import {
    DRAWING_LAYERS_BY_MODE,
    DRAWING_MODE_TO_GEOJSON_BOUNDARY_TYPE_MAP,
    GEOJSON_POLYGON_TYPE,
    GEOJSON_PROPERTY,
    MAP_ACTION_TYPE,
    MAP_EVENT_TYPE
} from './constants';
import type {
    DrawingMode,
    DrawingTool,
    Feature,
    InitializePointArgs,
    LineString,
    MapAction,
    MapVertexChangedEvent,
    Point,
    Polygon,
    Position,
    Props
} from './types';

const usePolygon = ({
    onCompleteFeature,
    onUpdateDrawing,
    onUpdatePolygonPoints: onPointsChanged
}: Props): DrawingTool => {
    const { subscribe, unsubscribe } = useMapEvents();

    const { features, mapState } = useMapContext();
    const { addFeature, removeFeature, updateFeature } = features;
    const { drawingMode, setIsDrawing } = mapState;

    const placedVerticesRef = useRef<Feature<Point>[]>([]);
    const placedLineStringRef = useRef<Feature<LineString>>(undefined);
    const firstToLastLineStringRef = useRef<Feature<LineString>>(undefined);

    const currentVertexRef = useRef<Feature<Point> | undefined>(undefined);
    const currentVertexDotRef = useRef<Feature<Point> | undefined>(undefined);
    const currentVertexLineStringRef = useRef<Feature<LineString> | undefined>(undefined);
    const currentPolygonFillRef = useRef<Feature<Polygon> | undefined>(undefined);
    const currentPolygonIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const handleVertexChanged = (event: MapVertexChangedEvent) => {
            const { vertexPosition } = event.detail;

            updateCurrentVertex(vertexPosition);
        };

        subscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);

        return () => {
            unsubscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        createLineStringFeature,
        createPointFeature,
        createPolygonFeature,
        generatePolygonFromVertices,
        hasFeatureWithId
    } = featureUtil;

    const getLayersByMode = () => {
        if (placedVerticesRef.current?.length === 0) {
            return DRAWING_LAYERS_BY_MODE[drawingMode];
        }

        const boundaryDrawingMode = placedVerticesRef.current[0].properties?.[
            GEOJSON_PROPERTY.BOUNDARY_TYPE
        ] as DrawingMode;

        return DRAWING_LAYERS_BY_MODE[boundaryDrawingMode];
    };

    const getCurrentVertexIndex = () =>
        placedVerticesRef.current.findIndex(feature => feature.id === currentVertexRef.current?.id);

    const isCurrentlyDrawing = () =>
        placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.IS_DRAWING] === true;

    const getPointsAroundCurrentPoint = () => {
        const currentVertexIndex = getCurrentVertexIndex();
        const totalVertices = placedVerticesRef.current.length;

        if (currentVertexIndex === -1) {
            // Current vertex is not yet placed, use first and last vertices
            return {
                nextPoint: placedVerticesRef.current[totalVertices - 1].geometry.coordinates,
                previousPoint: placedVerticesRef.current[0].geometry.coordinates
            };
        }

        const previousIndex = (currentVertexIndex - 1 + totalVertices) % totalVertices;
        const nextIndex = (currentVertexIndex + 1) % totalVertices;

        return {
            nextPoint: placedVerticesRef.current[nextIndex].geometry.coordinates,
            previousPoint: placedVerticesRef.current[previousIndex].geometry.coordinates
        };
    };

    const initializeNewDrawing = (initialPoint: Position) => {
        setIsDrawing(true);

        initializeFirstVertex({ id: uuidv4(), point: initialPoint });
        initializeCurrentVertex(initialPoint, uuidv4());

        currentPolygonIdRef.current = uuidv4();

        updateFromPlacedVertices();
        onPointsChanged?.(makePolygonFromVertices(currentPolygonIdRef.current as string), true);
    };

    const initializeFirstVertex = ({ drawingModeOverride, id, point }: InitializePointArgs) => {
        const newVertexFeature = createPointFeature(point, id);

        newVertexFeature.properties = {
            ...newVertexFeature.properties,
            [GEOJSON_PROPERTY.BOUNDARY_TYPE]:
                drawingModeOverride ?? DRAWING_MODE_TO_GEOJSON_BOUNDARY_TYPE_MAP[drawingMode],
            [GEOJSON_PROPERTY.IS_DRAWING]: true,
            [GEOJSON_PROPERTY.IS_SELECTED]: true
        };

        placedVerticesRef.current = [newVertexFeature];

        const { vertexLayer } = getLayersByMode();

        addFeature({
            feature: newVertexFeature,
            layerName: vertexLayer
        });

        placedVerticesRef.current = [newVertexFeature];
    };

    const initializeCurrentVertex = (point: Position, id: string) => {
        const newVertexFeature = createPointFeature(point, id);

        currentVertexRef.current = newVertexFeature;

        const { vertexLayer } = getLayersByMode();

        addFeature({
            feature: newVertexFeature,
            layerName: vertexLayer
        });
    };

    const updateFromPlacedVertices = () => {
        updatePlacedLineString();
        updatePlacedFirstToLastLineString();
    };

    const updatePlacedLineString = () => {
        const placedLineStringVertices = placedVerticesRef.current.map(
            feature => feature.geometry.coordinates
        );

        if (placedLineStringRef.current === undefined) {
            placedLineStringRef.current = createLineStringFeature(
                placedLineStringVertices,
                uuidv4()
            );

            const { polygonOutlineLayer } = getLayersByMode();

            addFeature({
                feature: placedLineStringRef.current,
                layerName: polygonOutlineLayer
            });
        }

        placedLineStringRef.current.geometry.coordinates = placedLineStringVertices;
        updateFeature(placedLineStringRef.current);
    };

    const updatePlacedFirstToLastLineString = () => {
        const firstVertex = placedVerticesRef.current[0];
        const lastVertex = placedVerticesRef.current[placedVerticesRef.current.length - 1];
        const firstToLastLineStringVertices = [
            firstVertex.geometry.coordinates,
            lastVertex.geometry.coordinates
        ];

        if (firstToLastLineStringRef.current === undefined) {
            firstToLastLineStringRef.current = createLineStringFeature(
                firstToLastLineStringVertices,
                uuidv4()
            );

            const { firstToLastLineStringLayer, polygonOutlineLayer } = getLayersByMode();

            const isNewDrawing =
                placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.IS_DRAWING] === true;
            const layer = isNewDrawing ? firstToLastLineStringLayer : polygonOutlineLayer;

            addFeature({
                feature: firstToLastLineStringRef.current,
                layerName: layer
            });
        }

        firstToLastLineStringRef.current.geometry.coordinates = firstToLastLineStringVertices;
        updateFeature(placedLineStringRef.current);
    };

    const updateCurrentVertex = (nextPosition: Position) => {
        if (!currentVertexRef.current) {
            return;
        }

        currentVertexRef.current.geometry.coordinates = nextPosition;
        updateFeature(currentVertexRef.current);

        updateFromCurrentVertex();
    };

    const updateFromCurrentVertex = () => {
        updateCurrentVertexLineString();
        updateCurrentPolygonFill();
        onUpdateDrawing?.(currentPolygonFillRef.current as Feature<Polygon>);
    };

    const updateCurrentVertexLineString = () => {
        if (!currentVertexRef.current) {
            return;
        }

        const currentPointPosition = currentVertexRef.current.geometry.coordinates;
        const { nextPoint, previousPoint } = getPointsAroundCurrentPoint();

        const currentVertexLineStringVertices = [previousPoint, currentPointPosition, nextPoint];

        if (currentVertexLineStringRef.current === undefined) {
            currentVertexLineStringRef.current = createLineStringFeature(
                currentVertexLineStringVertices,
                uuidv4()
            );

            const { currentVertexLineStringLayer } = getLayersByMode();

            addFeature({
                feature: currentVertexLineStringRef.current,
                layerName: currentVertexLineStringLayer
            });
        }

        currentVertexLineStringRef.current.geometry.coordinates = currentVertexLineStringVertices;
        updateFeature(currentVertexLineStringRef.current);
    };

    const updateCurrentPolygonFill = () => {
        const currentPolygonFillVertices = placedVerticesRef.current.map(
            feature => feature.geometry.coordinates
        );

        if (currentVertexRef.current) {
            const currentVertexIndex = getCurrentVertexIndex();

            if (currentVertexIndex === -1) {
                currentPolygonFillVertices.push(currentVertexRef.current.geometry.coordinates);
            }
        }

        // close the loop
        currentPolygonFillVertices.push(placedVerticesRef.current[0].geometry.coordinates);

        if (currentPolygonFillRef.current === undefined) {
            currentPolygonFillRef.current = createPolygonFeature(
                [currentPolygonFillVertices],
                currentPolygonIdRef.current as string
            );

            const { polygonFillLayer } = getLayersByMode();

            currentPolygonFillRef.current.properties = {
                ...currentPolygonFillRef.current.properties,
                [GEOJSON_PROPERTY.BOUNDARY_TYPE]:
                    placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE],
                [GEOJSON_PROPERTY.POLYGON_TYPE]: GEOJSON_POLYGON_TYPE.POLYGON,
                [GEOJSON_PROPERTY.IS_SELECTED]:
                    placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.IS_SELECTED]
            };

            addFeature({
                feature: currentPolygonFillRef.current,
                layerName: polygonFillLayer
            });
        }

        currentPolygonFillRef.current.geometry.coordinates = [currentPolygonFillVertices];
        updateFeature(currentPolygonFillRef.current);
    };

    const handleMapAction = (action: MapAction) => {
        const { type } = action;

        if (type !== MAP_ACTION_TYPE.POLYGON_UPDATE_POINTS) {
            return;
        }

        const { isNewDrawing, polygon } = action;

        loadDrawing(polygon, isNewDrawing);
    };

    const loadDrawing = (drawing: Feature<Polygon>, isNewDrawing: boolean = false) => {
        if (!drawing.id) {
            return;
        }

        resetDrawing();

        currentPolygonIdRef.current = drawing.id as string;

        // remove the closing point to get just the unique vertices
        const polygonVertices = drawing.geometry.coordinates[0].slice(0, -1);

        initializeFirstVertex({
            drawingModeOverride: drawing.properties?.[
                GEOJSON_PROPERTY.BOUNDARY_TYPE
            ] as DrawingMode,
            id: uuidv4(),
            point: polygonVertices[0]
        });

        if (isNewDrawing) {
            initializeCurrentVertex(polygonVertices[0], uuidv4());
        }

        if (placedVerticesRef.current && placedVerticesRef.current[0]) {
            placedVerticesRef.current[0].properties = {
                ...placedVerticesRef.current[0].properties,
                [GEOJSON_PROPERTY.IS_DRAWING]: isNewDrawing,
                [GEOJSON_PROPERTY.IS_SELECTED]: true
            };
        }

        for (const vertex of polygonVertices.slice(1)) {
            addPoint(vertex);
        }

        updateFromPlacedVertices();
        updateFromCurrentVertex();

        if (isNewDrawing) {
            setIsDrawing(true);
        }

        onPointsChanged?.(
            makePolygonFromVertices(currentPolygonIdRef.current as string),
            isNewDrawing
        );
        onUpdateDrawing?.(makePolygonFromVertices(currentPolygonIdRef.current as string));
    };

    const addPoint = (point: Position) => {
        const newVertexFeature = createPointFeature(point, uuidv4());

        const { vertexLayer } = getLayersByMode();

        addFeature({ feature: newVertexFeature, layerName: vertexLayer });

        placedVerticesRef.current = [...placedVerticesRef.current, newVertexFeature];

        updateFromPlacedVertices();
    };

    const handleClick = (point: Position, featuresUnderPoint?: Feature[]) => {
        const isDrawingInitialized = placedVerticesRef.current.length > 0;

        if (!isDrawingInitialized) {
            initializeNewDrawing(point);

            return;
        }

        if (isCurrentlyDrawing()) {
            if (isDrawingCompletable(featuresUnderPoint)) {
                completeDrawing();

                return;
            }

            addPoint(point);

            onPointsChanged?.(
                makePolygonFromVertices(currentPolygonIdRef.current as string),
                isCurrentlyDrawing()
            );

            return;
        }

        const isVertexSelected = getCurrentVertexIndex() >= 0;

        if (isVertexSelected) {
            handleDeselectVertex();

            return;
        }

        handleSelectedVertex(featuresUnderPoint);
    };

    const handleDeselectVertex = () => {
        currentVertexRef.current = undefined;

        updateFromPlacedVertices();
        updateFromCurrentVertex();

        onPointsChanged?.(makePolygonFromVertices(currentPolygonIdRef.current as string), false);

        completeDrawing();
    };

    const handleSelectedVertex = (featuresUnderPoint?: Feature[]) => {
        const vertexIds = placedVerticesRef.current.map(feature => feature.id as string);
        const matchingVertexIndex = vertexIds.findIndex(id =>
            hasFeatureWithId(featuresUnderPoint, id)
        );

        if (matchingVertexIndex >= 0) {
            setIsDrawing(true);
            currentVertexRef.current = placedVerticesRef.current[matchingVertexIndex];
        }
    };

    const completeDrawing = () => {
        if (!isMinimumPoints()) {
            return;
        }

        const finalPolygonFeature = makePolygonFromVertices(currentPolygonIdRef.current as string);

        onCompleteFeature(finalPolygonFeature);
    };

    const makePolygonFromVertices = (id: string) => {
        const polygon = generatePolygonFromVertices(placedVerticesRef.current, id);

        polygon.properties = {
            ...polygon.properties,
            [GEOJSON_PROPERTY.BOUNDARY_TYPE]:
                placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE],
            [GEOJSON_PROPERTY.POLYGON_TYPE]: GEOJSON_POLYGON_TYPE.POLYGON,
            [GEOJSON_PROPERTY.IS_SELECTED]:
                placedVerticesRef.current[0].properties?.[GEOJSON_PROPERTY.IS_SELECTED] ?? false
        };

        return polygon;
    };

    const isDrawingCompletable = (featuresUnderPoint?: Feature[]) => {
        const firstPoint = placedVerticesRef.current[0];
        const clickedOnFirstPoint = hasFeatureWithId(featuresUnderPoint, firstPoint.id as string);

        return isMinimumPoints() && clickedOnFirstPoint;
    };

    const isMinimumPoints = () =>
        placedVerticesRef.current && placedVerticesRef.current.length >= 3;

    const resetDrawing = () => {
        placedVerticesRef.current.forEach(feature => removeFeature(feature.id as string));

        removeFeature(placedLineStringRef.current?.id as string);
        removeFeature(firstToLastLineStringRef.current?.id as string);
        removeFeature(currentVertexRef.current?.id as string);
        removeFeature(currentVertexDotRef.current?.id as string);
        removeFeature(currentVertexLineStringRef.current?.id as string);
        removeFeature(currentPolygonFillRef.current?.id as string);

        resetInternalState();
        setIsDrawing(false);
    };

    const resetInternalState = () => {
        placedVerticesRef.current = [];
        placedLineStringRef.current = undefined;
        firstToLastLineStringRef.current = undefined;
        currentVertexRef.current = undefined;
        currentVertexDotRef.current = undefined;
        currentVertexLineStringRef.current = undefined;
        currentPolygonFillRef.current = undefined;
    };

    return {
        completeDrawing,
        handleClick,
        handleMapAction,
        isDrawing: true,
        loadDrawing,
        resetDrawing,
        updateNextPoint: updateCurrentVertex
    };
};

export default usePolygon;
